import * as React from 'react'
import styled, { css } from 'styled-components'
import { StyledFunctions } from '../utils'
import { Color, ButtonSize, IconButtonVariant } from '../types/styled'

type BorderRadius = 'oval' | 'none'

interface RootProps {
  size: ButtonSize
  style: React.CSSProperties
  variant: IconButtonVariant
  color: Color
  borderRadius: BorderRadius
  width: string
  clickable: boolean
}

const Root = styled.button<RootProps>`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${props => StyledFunctions.getIconButtonPadding(props.size)};
  min-width: ${props => props.width};
  max-width: ${props => props.width};
  height: ${props => {
    if (props.style.height) return props.style.height
    return StyledFunctions.getIconButtonSize(props.size)
  }};
  cursor: ${props => props.disabled || !props.clickable ? 'auto' : 'pointer'};
  background: ${props =>
    props.variant === 'contained' && props.disabled ? props.theme.color.button.bg.disabled
      : props.variant === 'contained' ? props.theme.color.icon[props.color]
        : 'transparent'};
  border-radius: ${props => props.borderRadius === 'oval' ? '50%' : 'none'};
  border: ${props => props.variant === 'outlined' ? '2px solid' : 'none'};
  border-color: ${props => props.theme.color.icon[props.color]};
  ${props => props.clickable && css`
    &:hover {
      background: ${props.theme.color.button.bgHover[props.disabled ? 'disabled' : props.color]};
    }
  `}

  &:focus {
    outline: none;
    box-shadow: none;
  }

  svg {
    width: 100%;
    height: 100%;

    path {
      fill: ${props =>
        props.variant === 'contained' && props.color === 'secondary' ? 'gray'
          : props.variant === 'contained' && props.color === 'mint' ? 'gray'
            : props.variant === 'contained' ? '#FFF'
              : props.theme.color.icon[props.color]};
    }
  }
`

interface Props {
  icon: React.ReactElement
  onClick: (e: React.MouseEvent) => void
  size?: ButtonSize
  variant?: IconButtonVariant
  color?: Color
  borderRadius?: BorderRadius
  style?: React.CSSProperties
  clickable?: boolean
  disabled?: boolean
}

const IconButton = (props: Props) => {
  const { icon, onClick, size = 'm', variant = 'base', color = 'primary', borderRadius = 'oval', style,
    disabled = false, clickable = true } = props

  const width: string = React.useMemo(() => {
    if (props.style?.width) return props.style.width + 'px'
    return StyledFunctions.getIconButtonSize(size)
  }, [size, props.style?.width])

  return (
    <Root
      type="button"
      onClick={disabled ? undefined : onClick}
      size={size}
      variant={variant}
      color={color}
      borderRadius={borderRadius}
      style={{ ...style }}
      disabled={disabled}
      width={width}
      clickable={clickable}
    >
      {icon}
    </Root>
  )
}

export { IconButton }